/** Values persisted on the document request (aligned with product naming). */
export const DOCUMENT_REQUEST_KIND = {
  NOTIFICATION: 'notification',
  AGREEMENT: 'agreement',
  CONTRACT_APPENDIX: 'contract_appendix',
} as const;

export type DocumentRequestKind =
  (typeof DOCUMENT_REQUEST_KIND)[keyof typeof DOCUMENT_REQUEST_KIND];

/** Values persisted on the document request status field. */
export const DOCUMENT_REQUEST_STATUS = {
  PENDING_REVIEW: 'pending_review',
  SIGNED: 'signed',
  REJECTED: 'rejected',
} as const;

export type DocumentRequestStatus =
  (typeof DOCUMENT_REQUEST_STATUS)[keyof typeof DOCUMENT_REQUEST_STATUS];

/** Agreement documentSubtype values — same strings persisted in Firestore and sent to Cloud Functions. */
export const AGREEMENT_SUBTYPE = {
  SHIFT_CHANGE: 'shift_change',
  WORKJOURNEY_CHANGE: 'work_journey_change',
  OVERTIME: 'overtime',
  OVERTIME_COMPENSATION: 'overtime_compensation',
} as const;

export type AgreementSubtype = (typeof AGREEMENT_SUBTYPE)[keyof typeof AGREEMENT_SUBTYPE];

/** Badge / rule grouping for subtype cards and max user selections. */
export const AGREEMENT_SUBTYPE_SEND_SCOPE = {
  MULTIPLE: 'multiple',
  INDIVIDUAL: 'individual',
} as const;

export type AgreementSubtypeSendScope =
  (typeof AGREEMENT_SUBTYPE_SEND_SCOPE)[keyof typeof AGREEMENT_SUBTYPE_SEND_SCOPE];

export type AgreementSubtypeCardDefinition = {
  value: AgreementSubtype;
  scopeDelivery: AgreementSubtypeSendScope;
  titleId: string;
  descriptionId: string;
};

/** Callable `compensationType` for overtime agreement (aligned with Cloud Functions enum). */
export const OVERTIME_COMPENSATION_TYPE = {
  ECONOMIC: 'economic',
  TIME_OFF: 'time_off',
} as const;

export type OvertimeCompensationTypeValue =
  (typeof OVERTIME_COMPENSATION_TYPE)[keyof typeof OVERTIME_COMPENSATION_TYPE];

/** Upper bound for confirmed overtime hours (step validation); confirm with product if needed. */
export const OVERTIME_CONFIRMED_HOURS_MAX = 200;

/** Holiday-rest hours granted per one confirmed overtime hour (art. 32 inc. 4° CT). */
export const OVERTIME_REST_HOLIDAY_HOURS_FACTOR = 1.5;

export function isUploadBasedDocumentKind(
  kind: DocumentRequestKind | null | undefined,
): kind is typeof DOCUMENT_REQUEST_KIND.NOTIFICATION | typeof DOCUMENT_REQUEST_KIND.CONTRACT_APPENDIX {
  return (
    kind === DOCUMENT_REQUEST_KIND.NOTIFICATION ||
    kind === DOCUMENT_REQUEST_KIND.CONTRACT_APPENDIX
  );
}

export function getAgreementSubtypeDefinition(
  subType: AgreementSubtype | undefined,
  definitions: AgreementSubtypeCardDefinition[],
): AgreementSubtypeCardDefinition | undefined {
  return definitions.find(d => d.value === subType);
}

export function getMaxSelectionsForAgreementSubtype(
  subType: AgreementSubtype | undefined,
  definitions: AgreementSubtypeCardDefinition[],
): number | undefined {
  const def = getAgreementSubtypeDefinition(subType, definitions);
  if (!def) return undefined;
  return def.scopeDelivery === AGREEMENT_SUBTYPE_SEND_SCOPE.INDIVIDUAL ? 1 : undefined;
}

/** True when the subtype is configured for a single recipient (matches card scope). */
export function isAgreementSubtypeIndividualScope(
  subType: AgreementSubtype | undefined,
  definitions: AgreementSubtypeCardDefinition[],
): boolean {
  const def = getAgreementSubtypeDefinition(subType, definitions);
  return def?.scopeDelivery === AGREEMENT_SUBTYPE_SEND_SCOPE.INDIVIDUAL;
}
